import * as Sentry from "@sentry/react";
import { isDevelopment } from "./environment";

interface LogContext {
  [key: string]: any;
}

/**
 * Centralized logger that uses Sentry Structured Logs
 * 
 * - In development: logs to console
 * - In production: sends logs directly to Sentry with structured attributes
 * - All logs are searchable and filterable in Sentry UI
 */
class Logger {
  /**
   * Debug logs - development only
   */
  debug(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.log(`🔍 ${message}`, context || "");
    } else {
      Sentry.logger.debug(message, context);
    }
  }

  /**
   * Info logs - sent to Sentry as structured logs
   */
  info(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.log(`ℹ️ ${message}`, context || "");
    } else {
      Sentry.logger.info(message, context);
    }
  }

  /**
   * Warning logs - sent to Sentry immediately
   */
  warn(message: string, context?: LogContext) {
    if (isDevelopment) {
      console.warn(`⚠️ ${message}`, context || "");
    } else {
      Sentry.logger.warn(message, context);
    }
  }

  /**
   * Error logs - sent to Sentry with full error tracking
   */
  error(message: string, error?: Error | unknown, context?: LogContext) {
    if (isDevelopment) {
      console.error(`❌ ${message}`, error, context || "");
    } else {
      if (error instanceof Error) {
        // Capture as exception for better error tracking
        Sentry.captureException(error, {
          extra: { message, ...context },
        });
      } else {
        // Log as error with context
        Sentry.logger.error(message, { error, ...context });
      }
    }
  }

  /**
   * Track custom events (e.g., business events like "Bing Ads triggered")
   * Sends immediately to Sentry as structured logs with searchable attributes
   */
  trackEvent(eventName: string, properties?: LogContext) {
    if (isDevelopment) {
      console.log(`📊 Event: ${eventName}`, properties || "");
    } else {
      Sentry.logger.info(eventName, {
        event_type: "tracking",
        ...properties,
      });
    }
  }

  /**
   * Set user context for all subsequent logs
   */
  setUser(user: { id: string | number; email?: string; [key: string]: any }) {
    if (!isDevelopment) {
      Sentry.setUser(user);
    }
  }

  /**
   * Clear user context (e.g., on logout)
   */
  clearUser() {
    if (!isDevelopment) {
      Sentry.setUser(null);
    }
  }

  /**
   * Add custom context that will be attached to all events
   */
  setContext(key: string, context: LogContext) {
    if (!isDevelopment) {
      Sentry.setContext(key, context);
    }
  }
}

export const logger = new Logger();

